import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, NavLink, useLocation, useNavigate } from 'react-router';
import { useHypoteseTellinger } from '../api/useHypoteseBehandling.ts';
import { useMoeteTellinger } from '../api/useMoeter.ts';
import { useInboxCounts } from '../api/useSubmissions.ts';
import { erHBehandler, H_ALLE, H_INNBOKS, H_INNSTILLINGER, H_MINE, H_MOETER, H_REGISTERET, H_TIL_DEG, hSiderFor, HYPOTESER, type HSide } from '../hypoteser/rommet.ts';
import { useMe } from '../api/useMe.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { GLOBALE_TASTER, Tastaturkart, type Tast } from '../innboks/Tastaturkart.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { KartProvider, type Kart } from './kart.tsx';
import { Merkehode } from './Merke.tsx';
import { Meny, MerKnapp } from './Meny.tsx';
import { Rombryter } from './Rombryter.tsx';
import { INNSPILL } from './rom.ts';
import { tilgangFra } from './tilgang.ts';
import { UtenRom } from './UtenRom.tsx';
import { useSidetittel } from './tittel.ts';
import { SmalSkjermProvider, useSmalSkjerm } from './useSmalSkjerm.ts';
import { useUtkastAntall, type Utkastantall } from '../innstillinger/utkast.tsx';

/**
 * Panelets skall etter skall-konsept v0.1, godkjent 2026-09-20 (R4.3). Kravlisten er
 * `design/wireframes/admin-skall.jsx` (DD-71).
 *
 * Over 900 px: sidemeny med merke + leietakernavn, rom-bryter (bare ved mer enn ett rom),
 * de fem sidene i DD-01-rekkefølge, Organisasjon under en skillelinje for eier, og en fot
 * som er ÉN linje — adressen og «⋯». Foten var 190 px med rolle, tre palett-knapper,
 * «Logg ut» og bygg på hver eneste side (F17); alt det bor nå i «⋯».
 *
 * Under 900 px: en stripe (`touch + sm` = 56) med merke + ordmerke · leietakernavn — eller
 * rom-bryteren, og da flytter identiteten inn i «⋯» — og en fanerad (`touch`) med de samme
 * fem sidene, som ruller sidelengs. Ingen fane kuttes; den valgte rulles inn ved lasting.
 * Fortsatt ÉN breakpoint (DD-51): faneraden ruller i stedet for å brekke.
 *
 * Leietakernavn, rolle og arbeidsrom kommer fra `GET /v1/admin/me`, som ikke bærer e-post.
 * Adressen er kontoens egen, fra identitetsleverandøren via AdminAuth-porten, og vises bare
 * til den som er logget inn (DD-48).
 */
/**
 * `ikon` er skallets ikonkart (R4.4, `admin-skall.jsx`): ett ikon per side, i sidemenyen og i
 * faneraden. Ikonet er DEKORATIVT — ordet står ved siden av i begge bredder, og lenkens
 * tilgjengelige navn er ordet alene (DD-08: form og farge bærer aldri mening alene).
 */
export const SIDER = [
  { navn: 'Innboks', sti: '/innboks', tast: 'i', ikon: 'file-tray-outline' },
  { navn: 'Saker', sti: '/saker', tast: 's', ikon: 'file-tray-full-outline' },
  { navn: 'Kjente feil', sti: '/kjente-feil', tast: 'k', ikon: 'warning-outline' },
  // P6: siden heter «Betaprogram» i sidemenyen og faneraden — første møte (DD-60 sier
  // «program» ellers). Snarveien `g p` og stien `/programmer` står.
  { navn: 'Betaprogram', sti: '/programmer', tast: 'p', ikon: 'flask-outline' },
  { navn: 'Innstillinger', sti: '/innstillinger', tast: 'n', ikon: 'settings-outline' },
] as const;

/**
 * Organisasjon er ikke i `SIDER`: den ligger UTENFOR rommene (DD-01), under en skillelinje
 * nederst i sidemenyen og bare for eier — og under 900 px i «⋯», aldri som en fane i rommet.
 */
export const ORGANISASJON = { navn: 'Organisasjon', sti: '/organisasjon', tast: 'o', ikon: 'business-outline' } as const;

/**
 * Rommet Hypoteser (H2.2, H2.3): sidene eies av `hypoteser/rommet.ts`, som gir dem etter rollen
 * i rommet (DD-01 presisert 2026-09-26). Skallet tegner dem som Innspills sider — samme rad,
 * samme merke, samme taster — og rom-bryteren bytter mellom rommene.
 */
export const erHModus = (sti: string) => sti === '/h' || sti.startsWith('/h/');

/**
 * Under 900 tegner skjemaet, kvitteringen, navnet og en hypotese i lista sin egen stripe med
 * tilbakepil (DD-51). Rommets lister — forsiden, Innboks, Til deg og Mine — har skallets stripe
 * og fanerad.
 */
/**
 * Innsenderens hypotese — /h/{nr} og /h/{nr}/endre — hører til «Mine hypoteser» (W8): den siden er
 * markert aktiv i sidemenyen og faneraden, selv om stien ikke ligger under /h/mine. Innboksens og
 * registerets detaljer (/h/innboks/{nr}, /h/registeret/{nr}) ligger under sin egen side og trenger
 * ikke dette.
 */
export const erHypotesenMin = (sti: string): boolean => /^\/h\/(h-)?\d+(\/endre)?\/?$/i.test(sti);
/** Oppstartskortet og konklusjonen — /h/{nr}/start og /h/{nr}/konklusjon — hører til «Til deg» (W10, W11): eierens sider, ikke innsenderens. */
export const erOppstart = (sti: string): boolean => /^\/h\/(h-)?\d+\/(start|konklusjon)\/?$/i.test(sti);
const aktivVia = (side: Side, sti: string): boolean => (side.sti === H_MINE.sti && erHypotesenMin(sti)) || (side.sti === H_TIL_DEG.sti && erOppstart(sti));

const H_LISTER = ['/h', H_INNBOKS.sti, H_TIL_DEG.sti, H_MINE.sti, H_REGISTERET.sti, H_MOETER.sti, H_INNSTILLINGER.sti];
// Innstillingenes undersider (statusnavn, koder, maler, beslutningstakere) er samme side med samme
// utkast og fot, og står i skallets stripe og fanerad som Innstillinger selv.
const hEgenStripe = (sti: string) => erHModus(sti) && !H_LISTER.includes(sti) && !sti.startsWith(`${H_INNSTILLINGER.sti}/`);

/** Innspills sider en bidragsyter uten rommet aldri ser (DD-01): adressen sender til rommet hennes. */
const INNSPILL_STIER = ['/innboks', '/saker', '/programmer', '/kjente-feil', '/innstillinger'];

type Side = (typeof SIDER)[number] | typeof ORGANISASJON | HSide;

/**
 * Organisasjons undersider er STEG i en trapp under 900 px (DD-01 presisert 2026-09-25):
 * «⋯» → Organisasjon → Panelbrukere → person. Stripen bærer da tilbakepilen og sidens navn i
 * stedet for merket, og faneraden finnes ikke — Organisasjon er ikke en fane i rommet.
 */
export interface Steg {
  tittel: string;
  til: string;
  href: string;
}

export function stegFor(sti: string): Steg | null {
  if (sti === '/organisasjon/brukere') return { tittel: 'Panelbrukere', til: 'Organisasjon', href: '/organisasjon' };
  if (sti === '/organisasjon/brukere/ny') return { tittel: 'Legg til person', til: 'Panelbrukere', href: '/organisasjon/brukere' };
  if (sti.startsWith('/organisasjon/brukere/')) return { tittel: 'Endre tilgang', til: 'Panelbrukere', href: '/organisasjon/brukere' };
  return null;
}

/**
 * Merket i sidemenyen (F23). Produkteier, etter å ha sendt inn en sak fra widgeten:
 * «Burde vel se et antall utenfor Innboks og Saker for å indikere at det er noe der.»
 * Tellingen fantes kun i sidens egen topp, altså bare på siden du allerede sto på.
 *
 * Tre valg, og de henger sammen:
 *
 * 1. **Bare Innboks.** Saker er den samme mengden, browset — et merke der ville
 *    doblet tallet på raden over. Betaprogram og Kjente feil har LANGLIVEDE tilstander
 *    («aktiv» kan stå i måneder), så et tall der er en statusavlesning og ikke et
 *    varsel; et merke som alltid står der slutter å bety «noe er nytt». Konseptet tegner
 *    et tall på Betaprogram også — det er utelatt her fordi kontrakten ikke har tellingen:
 *    verken `/me` eller `InboxCounts` bærer den, og programlista er paginert, så et tall
 *    regnet i nettleseren ville vært feil forbi første side.
 * 2. **`unhandled`, ikke totalen.** Produkteier spurte «antall vs ubehandlet».
 *    Totalen går aldri ned og blir dekor; `unhandled` er det som venter på en avgjørelse.
 *    Ordet på skjermen er «til vurdering», IKKE «ubehandlet» (DD-52): mengden er
 *    statusgruppen `reviewing`, som er Mottatt OG Vurderes — en sak man nettopp har
 *    satt til Vurderes teller fortsatt, og «ubehandlet» lovet at ingen hadde rørt den.
 * 3. **Skjult ved null.** Samme grunn som 1: fraværet av merket er selv informasjonen.
 *
 * Fargen bærer ikke mening alene (DD-08): tallet står der, og lenkens tilgjengelige
 * navn sier «Innboks — 6 til vurdering, 1 over frist».
 */
export interface Merke {
  tall: number;
  /** Hva tallet ER. Går inn i lenkens tilgjengelige navn — fargen bærer aldri mening alene. */
  hva: string;
  /** `venter` = saker som venter på deg. `ulagret` = dine egne endringer som ikke er sendt. */
  slag: 'venter' | 'ulagret';
  /** Eget varsel under tallet, i warning med ikon (DD-08) — aldri bare et gult tall. */
  sub?: { tall: number; tekst: string };
}

/**
 * To merker, to betydninger, og de skal ikke forveksles:
 *
 * - **Innboks** bærer saker som venter på en avgjørelse. De kommer utenfra, og «over frist»
 *   er et eget varsel under tallet — ikke en farge på tallet.
 * - **Innstillinger og Organisasjon** bærer DINE ulagrede endringer (F32). Utkastet overlever
 *   at du går til en annen side, så uten dette merket kunne det bli liggende ubemerket — det
 *   er prisen for å fjerne tapet i stedet for å advare om det, og merket er det som betaler.
 *   Det kommer fra utkastet i nettleseren, og R5.3 ga de to sidene hvert sitt utkast (P3):
 *   skallet leser begge tallene, så et utkast på Organisasjon synes fra Innstillinger og
 *   omvendt.
 *
 * Begge er skjult ved null: et merke som alltid står der slutter å bety «se her».
 */
function merkeFor(sti: string, tilVurdering: number | undefined, overFrist: number | undefined, ulagret: Utkastantall, h?: { tilVurdering: number; avgjoerNaa: number; tilDeg: number; moeter?: number }): Merke | null {
  if (sti === '/innboks' && tilVurdering) {
    return { tall: tilVurdering, hva: 'til vurdering', slag: 'venter', ...(overFrist ? { sub: { tall: overFrist, tekst: `${overFrist} over frist` } } : {}) };
  }
  // Rommet Hypoteser (DD-52, `admin-skall.jsx`): samme ord som i Innspill, og «avgjør nå» under i
  // warning med ikon, som «over frist» — et varsel, ikke en statusavlesning.
  if (sti === H_INNBOKS.sti && h?.tilVurdering) {
    return { tall: h.tilVurdering, hva: 'til vurdering', slag: 'venter', ...(h.avgjoerNaa ? { sub: { tall: h.avgjoerNaa, tekst: `${h.avgjoerNaa} avgjør nå` } } : {}) };
  }
  // «Til deg» (2026-10-08): `requiresYou` er samme mengde som lista — merket og lista viser samme tall.
  if (sti === H_TIL_DEG.sti && h?.tilDeg) return { tall: h.tilDeg, hva: 'venter på deg', slag: 'venter' };
  // H3.2 (W1): «Møter» bærer de planlagte møtene — et tall på det som kommer, ikke et varsel.
  if (sti === H_MOETER.sti && h?.moeter) return { tall: h.moeter, hva: 'planlagt', slag: 'venter' };
  const utkast = sti === '/innstillinger' ? ulagret.innstillinger : sti === '/organisasjon' ? ulagret.organisasjon : sti === H_INNSTILLINGER.sti ? ulagret.hypoteser : 0;
  if (utkast) return { tall: utkast, hva: utkast === 1 ? 'endring ikke lagret' : 'endringer ikke lagret', slag: 'ulagret' };
  return null;
}

/** Begge tallene med ord, i ÉN etikett (avvik 22). `undefined` når raden ikke har et merke. */
function navnMed(side: Side, merke: Merke | null): string | undefined {
  if (merke === null) return undefined;
  return `${side.navn} — ${merke.tall} ${merke.hva}${merke.sub ? `, ${merke.sub.tekst}` : ''}`;
}

/** Tallet. Det ulagrede bærer pennen, så to merker med ulik betydning ikke skilles på farge alene (DD-08). */
function Tall({ merke, tast }: { merke: Merke; tast: string }) {
  return (
    <span className={`panel-nav-merke panel-nav-merke-${merke.slag}`} aria-hidden="true" data-testid={`nav-merke-${tast}`}>
      {merke.slag === 'ulagret' ? <Ikon navn="create-outline" /> : null}
      {merke.tall}
    </span>
  );
}

/**
 * Én rad i sidemenyen. `merke` er ALLEREDE avgjort av merkeFor — her sjekkes `!== null` og
 * ikke sannhetsverdi, så et merke på 0 ville vist seg som en synlig null i stedet for å
 * forsvinne stille. Regelen skal bo ett sted, og det stedet er merkeFor.
 */
function Nav({ side, merke }: { side: Side; merke: Merke | null }) {
  const sti = useLocation().pathname;
  // NavLink regner aktiv fra stien alene; en side som eier stier utenfor seg (erHypotesenMin) settes her.
  const Lenke = aktivVia(side, sti) ? Link : NavLink;
  return (
    <Lenke to={side.sti} className="panel-nav-lenke" title={`Snarvei: g ${side.tast}`} aria-keyshortcuts={`g ${side.tast}`} aria-label={navnMed(side, merke)} aria-current={aktivVia(side, sti) ? 'page' : undefined}>
      <Ikon navn={side.ikon} />
      <span className="panel-nav-tekst">
        <span className="panel-nav-navn">{side.navn}</span>
        {merke?.sub ? (
          <span className="panel-nav-sub" aria-hidden="true">
            <Ikon navn="warning-outline" /> {merke.sub.tekst}
          </span>
        ) : null}
      </span>
      {merke !== null ? <Tall merke={merke} tast={side.tast} /> : null}
      <kbd aria-hidden="true">g {side.tast}</kbd>
    </Lenke>
  );
}

/**
 * Faneraden under 900 px: de samme fem sidene, samme tall, samme `aria-current`. Den ruller
 * sidelengs når sidene ikke får plass — ingen fane kuttes med prikker (avvik 17), og den
 * valgte rulles inn ved lasting. Rullingen regnes på BEHOLDEREN og ikke med
 * `scrollIntoView`: den siste ville flyttet siden bak faneraden også.
 */
function Fanerad({ sider, merker, valgt }: { sider: readonly Side[]; merker: Array<Merke | null>; valgt: string }) {
  const boks = useRef<HTMLElement>(null);
  const valgtRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const el = valgtRef.current;
    const b = boks.current;
    if (!el || !b) return;
    // Luften rundt den valgte fanen er avstandsskalaens, ikke et tall her (CLAUDE.md,
    // «Styling»): tokenet leses fra dokumentet, som breakpointen i useSmalSkjerm.
    const luft = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue('--innspel-space-sm'), 10) || 0;
    const venstre = el.offsetLeft - luft;
    const hoyre = el.offsetLeft + el.offsetWidth + luft;
    if (venstre < b.scrollLeft) b.scrollLeft = venstre;
    else if (hoyre > b.scrollLeft + b.clientWidth) b.scrollLeft = hoyre - b.clientWidth;
  }, [valgt]);

  return (
    <nav ref={boks} aria-label="Sider" className="panel-fanerad">
      {sider.map((side, i) => {
        const merke = merker[i] ?? null;
        const via = aktivVia(side, valgt);
        const Lenke = via ? Link : NavLink;
        return (
          <Lenke
            key={side.sti}
            ref={via || valgt === side.sti || valgt.startsWith(`${side.sti}/`) ? valgtRef : undefined}
            to={side.sti}
            className="panel-fane"
            aria-keyshortcuts={`g ${side.tast}`}
            aria-label={navnMed(side, merke)}
            aria-current={via ? 'page' : undefined}
          >
            <Ikon navn={side.ikon} />
            <span className="panel-nav-navn">{'kort' in side ? side.kort : side.navn}</span>
            {merke !== null ? <Tall merke={merke} tast={side.tast} /> : null}
            {merke?.sub ? (
              <span className="panel-fane-varsel" aria-hidden="true">
                <Ikon navn="warning-outline" />
                {merke.sub.tall}
              </span>
            ) : null}
            <kbd aria-hidden="true">g {side.tast}</kbd>
          </Lenke>
        );
      })}
    </nav>
  );
}

export function Skall({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const me = useMe();
  // Skallet leser `access`, aldri `role`, som bare sier eier eller medlem (M4.8): rom-bryteren teller rommene
  // brukeren har, og et medlem uten rom får et skall uten sider.
  const tilgang = tilgangFra(me.data);
  const tellinger = useInboxCounts(tilgang.kanSe(INNSPILL));
  const ulagret = useUtkastAntall();
  const naviger = useNavigate();
  const sti = useLocation().pathname;
  const g = useRef<number | null>(null);
  const smal = useSmalSkjerm();

  const [meny, setMeny] = useState(false);
  const [kartAapent, setKartAapent] = useState(false);
  const [sidensKart, setSidensKart] = useState<{ tittel: string; taster: Tast[] } | null>(null);
  const menyKnapp = useRef<HTMLButtonElement>(null);

  const rom = tilgang.rom;
  const eier = tilgang.eier;
  const utenRom = tilgang.utenRom;
  // Trappen finnes bare for eier: for et medlem er undersiden «Denne siden er for eier».
  const steg = eier ? stegFor(sti) : null;
  const adresse = auth.tilstand.navn === 'innlogget' && auth.tilstand.epost ? auth.tilstand.epost : null;

  // Rommet siden står i leses av adressen (H2.3): /h/* er rommet Hypoteser, resten er Innspill.
  // Et medlem uten Innspill står alltid i Hypoteser — også på Organisasjon og Min innlogging —
  // så sidemenyen aldri viser et rom hun ikke har (DD-01). Samme regel motsatt vei: en /h/*-adresse
  // hos en leietaker eller en bruker uten Hypoteser tegnes i Innspill, med meldingen fra HModus i
  // midten — ikke rommets meny rundt et rom som ikke finnes (sett i drift 2026-10-04). Mens «meg»
  // ennå ikke er kjent, står adressen, så en med rommet ikke ser Innspill blinke forbi.
  const hModus = (erHModus(sti) && (!tilgang.kjent || tilgang.kanSe(HYPOTESER))) || (tilgang.kjent && !tilgang.kanSe(INNSPILL) && tilgang.kanSe(HYPOTESER));
  const aktivtRom = hModus ? HYPOTESER : INNSPILL;
  const hSider = useMemo(() => hSiderFor(tilgang), [tilgang]);
  const romSider: readonly Side[] = hModus ? hSider : SIDER;
  // Rom-bryteren BYTTER rom (H2.3): hvert rom har sin egen forside, og /h velger rommets etter rollen.
  const velgRom = useCallback((r: string) => void naviger(r === HYPOTESER ? '/h' : '/innboks'), [naviger]);
  // Tellingen er bidragsyter i kontrakten: «Til deg» har et merke for alle i rommet, innboksen bare for behandlere.
  // H3.3: merket er requiresYou + sourceQuestionsForYou (spørsmålene om kildene den innloggede eier).
  const hTellinger = useHypoteseTellinger(hModus && tilgang.kanSe(HYPOTESER));
  // H3.2: «Til deg» teller også møtet du skal bekrefte og begrunnelsene du mangler; «Møter» de planlagte.
  const mTellinger = useMoeteTellinger(hModus && tilgang.kjent && tilgang.kanSe(HYPOTESER));
  const m = mTellinger.data;
  const hMerke = hTellinger.data ? { tilVurdering: erHBehandler(tilgang) ? hTellinger.data.received + hTellinger.data.reviewing : 0, avgjoerNaa: hTellinger.data.dueNow, tilDeg: hTellinger.data.requiresYou + (hTellinger.data.sourceQuestionsForYou ?? 0) + (m ? m.toConfirm + m.reasonsMissing : 0), moeter: m?.planned ?? 0 } : undefined;

  const alle: Side[] = useMemo(() => [...SIDER, ORGANISASJON, ...H_ALLE], []);
  useSidetittel(steg?.tittel ?? alle.find((s) => s.sti === sti)?.navn ?? (hModus ? 'Hypoteser' : undefined), me.data?.tenantName);

  const lukkMeny = useCallback(() => {
    setMeny(false);
    menyKnapp.current?.focus();
  }, []);

  // Kartets tilstand og taster bor i skallet (P4) — sidene melder bare inn hva det viser.
  const kart: Kart = useMemo(
    () => ({
      meldInn: (tittel, taster) => setSidensKart({ tittel, taster }),
      rydd: () => setSidensKart(null),
      aapne: () => setKartAapent(true),
    }),
    [],
  );

  // g i · g s · g k · g p · g n, og g o for eier. Aldri mens et felt har fokus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mål = e.target as HTMLElement | null;
      if (mål && /^(INPUT|TEXTAREA|SELECT)$/.test(mål.tagName)) return;
      if (e.key === 'g') {
        g.current = Date.now();
        return;
      }
      if (g.current && Date.now() - g.current < 1500) {
        // Uten rom finnes ingen sider å gå til — heller ikke med tastene.
        // I rommet Hypoteser er det rommets sider (g i · g t · g m); en side personen ikke har,
        // har heller ingen tast (admin-skall.jsx, TASTATURFLYTEN).
        const sider: Side[] = utenRom ? [] : [...romSider];
        const side = [...sider, ...(eier ? [ORGANISASJON] : [])].find((s) => s.tast === e.key);
        g.current = null;
        if (side) void naviger(side.sti);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [naviger, eier, utenRom, romSider]);

  // `?` åpner kartet i begge bredder (DD-11 presisert). Aldri mens et felt har fokus —
  // `?` er et tegn man kan skrive. Dialogen selv blokkerer tasten mens den står åpen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey) return;
      const mål = e.target as HTMLElement | null;
      if (mål && /^(INPUT|TEXTAREA|SELECT)$/.test(mål.tagName)) return;
      e.preventDefault();
      setKartAapent(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const merker = romSider.map((s) => merkeFor(s.sti, tellinger.data?.unhandled, tellinger.data?.overdue, ulagret, hMerke));
  const bygg = `Panel ${__PANEL_VERSJON__} · ${__PANEL_SHA__}`;
  const flereRom = rom.length > 1;

  // En bidragsyter uten rommet Innspill på en av Innspills adresser: til rommet hennes (DD-01).
  if (tilgang.kjent && !tilgang.kanSe(INNSPILL) && tilgang.kanSe(HYPOTESER) && INNSPILL_STIER.some((s) => sti === s || sti.startsWith(`${s}/`))) return <Navigate to="/h" replace />;

  return (
    <div className={smal ? 'panel panel-smal' : 'panel'}>
      <a href="#innhold" className="panel-hopp">
        Hopp til innhold
      </a>

      {smal && hEgenStripe(sti) ? null : smal ? (
        <>
          {/* Stripen: merke + ordmerke · leietakernavn — eller rom-bryteren, og da står
              identiteten øverst i «⋯» (DD-48). Søk er sidens ikon, aldri stripens (DD-02). */}
          {/* <header> og ikke <div>: under grensen er stripen skallets eneste landemerke
              over faneraden, og alt innhold skal ligge i et (axe «region»). Sidenes egen
              `.panel-topp` er også en <header>, men den står inne i <main> og blir ingen
              banner — det er bare denne ene. */}
          <header className="panel-stripe" data-testid="stripe">
            {steg && !utenRom ? (
              <>
                <Link to={steg.href} className="panel-stripe-tilbake" aria-label={`Tilbake til ${steg.til}`}>
                  <Ikon navn="chevron-back-outline" />
                </Link>
                <span className="panel-stripe-tittel">{steg.tittel}</span>
              </>
            ) : flereRom ? (
              <Rombryter rom={rom} valgt={aktivtRom} onVelg={velgRom} smal />
            ) : (
              <Merkehode storrelse="md" linje>
                <span className="panel-merke-skille" aria-hidden="true">
                  ·
                </span>
                <span className="panel-merke-leietaker">{me.data?.tenantName ?? ' '}</span>
              </Merkehode>
            )}
            <MerKnapp aapen={meny} knappRef={menyKnapp} onClick={() => (meny ? lukkMeny() : setMeny(true))} />
          </header>
          {/* Ingen fanerad på et steg i trappen, og ingen for et medlem uten rom (DD-01). I rommet
              Hypoteser står den på rommets lister; skjemaet og en hypotese har egen stripe (over). */}
          {steg || utenRom ? null : <Fanerad sider={romSider} merker={merker} valgt={sti} />}
        </>
      ) : (
        <aside aria-label="Sidemeny" className="panel-side">
          {/* Alt over foten ruller; foten står. Uten den delingen ville «⋯» blitt klippet
              av `overflow-y: auto` i et kort vindu (avvik 14). */}
          <div className="panel-side-rull">
            <Merkehode storrelse="lg">
              <div className="panel-merke-leietaker">{me.data?.tenantName ?? ' '}</div>
            </Merkehode>
            {utenRom ? null : (
              <>
                {flereRom ? <Rombryter rom={rom} valgt={aktivtRom} onVelg={velgRom} /> : null}
                {/* «Ny hypotese» er en KNAPP over rommets sider for alle roller, ikke en side (P3). */}
                {hModus ? <NyHypotese /> : null}
                <nav aria-label="Sider" className="panel-nav">
                  {romSider.map((s, i) => (
                    <Nav key={s.sti} side={s} merke={merker[i] ?? null} />
                  ))}
                </nav>
              </>
            )}
            {/* Verken lenken eller skillelinjen finnes i DOM for behandler og leser (DD-01).
                «Alle rom» står bare med flere rom, og uten rolleord (avvik 18). Etiketten tåler rom
                tre (DD-01 presisert 2026-09-26, preferanse 2026-09-25) — den het «Begge rom». */}
            {eier ? (
              <div className="panel-side-org">
                {flereRom ? <span className="panel-side-org-etikett">Alle rom</span> : null}
                <Nav side={ORGANISASJON} merke={merkeFor(ORGANISASJON.sti, undefined, undefined, ulagret)} />
              </div>
            ) : null}
          </div>
          <div className="panel-side-fot">
            {adresse ? <span className="panel-adresse">{adresse}</span> : <span className="panel-adresse">&nbsp;</span>}
            <MerKnapp aapen={meny} knappRef={menyKnapp} onClick={() => (meny ? lukkMeny() : setMeny(true))} />
          </div>
        </aside>
      )}

      <main id="innhold" tabIndex={-1} className="panel-innhold">
        <SmalSkjermProvider value={smal}>
          {/* Min innlogging er brukerens egen konto og står også uten rom; alt annet er rommets. */}
          {/* Rommets sider venter på «meg»: før tilgangen er kjent, vet ingen om et kall i rommet
              vil slippe inn (ukjent er strengest). Min innlogging er brukerens egen konto og står
              også uten rom. Feiler «meg», avgjør Sesjonsvakt — sidene tegnes som før. */}
          <KartProvider value={kart}>
            {sti === '/min-innlogging' || me.isError ? (
              children
            ) : me.isPending ? (
              <div role="status" aria-busy="true" aria-label="Laster" className="panel-skjelett">
                <span className="panel-skjelett-linje panel-skjelett-tittel" />
                <span className="panel-skjelett-linje" />
              </div>
            ) : utenRom ? (
              <UtenRom leietaker={me.data?.tenantName} />
            ) : (
              children
            )}
          </KartProvider>
        </SmalSkjermProvider>
      </main>

      {/* Menyen er et SØSKEN av sidemenyen og av sidens flate — aldri et barn av et
          rullende element (P5, avvik 14). */}
      {meny ? (
        <Meny
          smal={smal}
          eier={eier}
          flereRom={flereRom}
          rolle={tilgang.rolletekst}
          visningsnavn={tilgang.kanSe('hypoteser') ? { navn: me.data?.displayName, onEndre: () => { lukkMeny(); void naviger('/h/navn'); } } : undefined}
          veiledning={tilgang.kanSe('hypoteser')}
          utenRom={utenRom}
          adresse={adresse}
          leietaker={me.data?.tenantName}
          bygg={bygg}
          onLukk={lukkMeny}
          onLoggUt={() => {
            lukkMeny();
            void auth.loggUt();
          }}
          onKart={() => {
            lukkMeny();
            setKartAapent(true);
          }}
        />
      ) : null}

      {kartAapent ? <Tastaturkart tittel={sidensKart?.tittel ?? (hModus ? 'Hypoteser' : 'Panelet')} taster={sidensKart?.taster ?? []} globale={hModus ? H_TASTER(hSider, eier) : GLOBALE_TASTER(eier)} onLukk={() => setKartAapent(false)} /> : null}
    </div>
  );
}

/**
 * Rommets taster (DD-11 presisert 2026-09-26): g + sidens bokstav for sidene personen HAR — en
 * side hun ikke har, har heller ingen tast i kartet (admin-skall.jsx, TASTATURFLYTEN).
 */
const H_TASTER = (sider: readonly HSide[], eier: boolean): Tast[] => [
  [sider.map((s) => `g ${s.tast}`).join(' / '), `gå til ${sider.map((s) => s.navn).join(' / ')}`],
  ...(eier ? ([['g o', 'gå til Organisasjon']] as Tast[]) : []),
  ['?', 'dette kartet'],
];

/**
 * «Ny hypotese» i sidemenyen: OMRISS over sidene (produkteier 2026-09-26, DD-58 — foten har det
 * ene primærvalget, også mens skjemaet er åpent), og ingen snarvei på den (DD-11). Veien tilbake
 * til rommet Innspill er rom-bryteren over, som finnes for den som har begge rom.
 */
function NyHypotese() {
  return (
    <Link to="/h/ny" className="h-ny-knapp" data-testid="h-ny">
      <Ikon navn="add-outline" />
      Ny hypotese
    </Link>
  );
}
